import {
  Box,
  Button,
  CloudImage,
  Divider,
  Heading,
  Stack,
  useIntersectionObserver,
  useTheme,
} from "@biom3/react";
import type { Content } from "@prismicio/client";

import { type WishlistProps, useIsLessThanMediumScreen } from "@ui-kit";
import { PlayButton } from "@ui-kit/CTAButtons/PlayButton";
import { WishlistButton } from "@ui-kit/CTAButtons/WishlistButton";
import { trackGameDetailsEvent } from "@ui-kit/utils";
import { useMemo } from "react";

type PremiumGameHeaderProps = {
  stickyPageTitle?: string;
  game: Content.GameDocument;
  wishlistProps?: WishlistProps;
  playLink: string;
  useMobileExperiment?: boolean;
  showDivider?: boolean;
};

const MOTION_SX = {
  transitionDuration: "base.motion.normal.gentle.cssDuration",
  transitionTimingFunction: "base.motion.normal.gentle.cssTimingFunction",
};

export const PremiumGameHeader = ({
  game,
  wishlistProps,
  playLink,
  stickyPageTitle,
  showDivider = true,
}: PremiumGameHeaderProps) => {
  const { base } = useTheme();
  const { entry, domRef } = useIntersectionObserver({
    threshold: 1,
  });
  const isLessThanMedium = useIsLessThanMediumScreen();
  const isSticky = useMemo(() => {
    if (!entry) return false;
    return isLessThanMedium === false && entry?.intersectionRatio < 1;
  }, [entry, isLessThanMedium]);
  const onPlayButtonClick = (link: string) => {
    trackGameDetailsEvent({
      game,
      identifier: "PremiumGameHeader",
      control: "PlayNowClicked",
      controlType: "Button",
      extras: {
        website: link,
      },
    });
  };

  const onDownloadClick = () => {
    trackGameDetailsEvent({
      game,
      identifier: "PremiumGameHeader",
      control: "DownloadClicked",
      controlType: "Button",
      extras: {
        website: "https://dw.ravenquest.io/ravenquest_installer.exe",
      },
    });
    window.open("https://dw.ravenquest.io/ravenquest_installer.exe", "_blank");
  };

  const showPageTitle = stickyPageTitle && isSticky;
  return (
    <Box
      sx={{
        pos: { default: "static", medium: "sticky" },
        px: {
          default: "unset",
          medium: isSticky ? "base.spacing.x7" : "0px",
          large: isSticky ? "base.spacing.x18" : "base.spacing.x12",
        },
        pr: {
          default: "unset",
          medium: isSticky ? "base.spacing.x5" : undefined,
          large: isSticky ? "base.spacing.x22" : undefined,
        },
        top: "-1px",
        mx: {
          default: "unset",
          medium: isSticky ? `-${base.spacing.x6}` : "0px",
        },
        w: {
          default: "100%",
          medium: isSticky ? `calc(100% + ${base.spacing.x12})` : "100%",
          large: isSticky ? `calc(100% + ${base.spacing.x16})` : "100%",
        },
        zIndex: isSticky ? 4 : "unset",
        backdropFilter: isSticky ? "base.frost.200" : "unset",
        transitionProperty: "padding",
        ...MOTION_SX,

        "&::before": {
          content: isSticky ? '""' : "unset",
          position: "absolute",
          top: "0px",
          left: "0px",
          w: "100%",
          h: "180%",
          zIndex: -1,
          bg: `linear-gradient(180deg, ${base.color.neutral[700]} 0%, transparent 100%)`,
        },
      }}
      className="PremiumGameHeader"
      domRef={domRef}
    >
      <Stack
        direction="row"
        justifyContent={isLessThanMedium ? "center" : "flex-start"}
        alignItems="center"
        sx={{
          transitionProperty: "padding",
          ...MOTION_SX,
          pt: {
            default: isLessThanMedium ? "" : "base.spacing.x2",
            medium: isSticky ? "base.spacing.x2" : "base.spacing.x14",
          },
          pb: isSticky
            ? "base.spacing.x2"
            : isLessThanMedium
              ? "base.spacing.x2"
              : "base.spacing.x6",
        }}
      >
        {game.data.logo_intro.url && (
          <CloudImage
            imageUrl={game.data.logo_intro.url}
            relativeImageSizeInLayout="240px"
            sx={{
              flexShrink: 0,
              w: {
                default: isLessThanMedium ? "100px" : "80px",
                medium: "unset",
              },
              maxw: { default: "180px", medium: isSticky ? "180px" : "240px" },
              maxh: { default: "60px", medium: isSticky ? "52px" : "96px" },
              objectFit: "contain",
              zIndex: 1,
              transitionProperty: "width, height",
              ...MOTION_SX,
            }}
          />
        )}

        {showPageTitle && stickyPageTitle && (
          <Heading
            size="small"
            sx={{ d: { default: "none", xLarge: "block" } }}
          >
            {stickyPageTitle}
          </Heading>
        )}

        {game.uid === "ravenquest" && !isLessThanMedium && (
          <Button
            onClick={onDownloadClick}
            testId="header-download-button"
            size="medium"
            variant="primary"
            sx={{
              ml: "base.spacing.x4",
              "&:hover": {
                bg: "base.color.accent.700",
                transform: "scale(1.02)",
              },
              transition:
                "transform 0.2s ease-in-out, background-color 0.2s ease-in-out",
            }}
          >
            Download Game
          </Button>
        )}

        <Stack
          direction="row"
          sx={{ d: { default: "none", medium: "flex" }, ml: "auto" }}
        >
          <PlayButton
            buttonSize="small"
            playLink={playLink}
            analyticsEvent={onPlayButtonClick}
          />
          {wishlistProps && (
            <WishlistButton
              trackGameDetailsEventIdentifier="PremiumGameHeader"
              buttonSize="small"
              wishlistProps={wishlistProps}
            />
          )}
        </Stack>
      </Stack>
      {!isLessThanMedium && showDivider && <Divider />}
    </Box>
  );
};
